import React from 'react';
import {
  Users as UsersIcon,
  CreditCard,
  Clock,
  TrendingUp,
} from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useDashboard } from '../hooks/useDashboard';
import StatCard from '../components/ui/StatCard';
import Card from '../components/ui/Card';
import Badge, { statusVariant } from '../components/ui/Badge';
import { StatCardSkeleton } from '../components/ui/Skeleton';
import Skeleton from '../components/ui/Skeleton';
import { formatDate, formatCurrency } from '../utils/formatters';

export default function Dashboard() {
  const { data, isLoading } = useDashboard();

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <StatCardSkeleton key={i} />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="p-6"><Skeleton className="w-full h-60" /></Card>
          <Card className="p-6"><Skeleton className="w-full h-60" /></Card>
        </div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-6">
      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Users"
          value={data.totalUsers}
          change={data.userChange}
          icon={<UsersIcon className="w-5 h-5 text-blue-600" />}
          iconColor="bg-blue-100"
        />
        <StatCard
          title="Active Subscribers"
          value={data.activeSubscribers}
          change={data.subscriberChange}
          icon={<CreditCard className="w-5 h-5 text-green-600" />}
          iconColor="bg-green-100"
        />
        <StatCard
          title="Trial Users"
          value={data.trialUsers}
          change={data.trialChange}
          icon={<Clock className="w-5 h-5 text-amber-600" />}
          iconColor="bg-amber-100"
        />
        <StatCard
          title="Revenue This Month"
          value={formatCurrency(data.revenueThisMonth)}
          change={data.revenueChange}
          icon={<TrendingUp className="w-5 h-5 text-purple-600" />}
          iconColor="bg-purple-100"
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-6">
          <h3 className="text-base font-semibold text-text-primary mb-4">
            Daily Signups (Last 30 Days)
          </h3>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={data.dailySignups}>
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11, fill: '#94A3B8' }}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fill: '#94A3B8' }}
                tickLine={false}
                axisLine={false}
                width={30}
              />
              <Tooltip
                contentStyle={{
                  background: '#fff',
                  border: '1px solid #E2E8F0',
                  borderRadius: 10,
                  fontSize: 13,
                }}
              />
              <Line
                type="monotone"
                dataKey="count"
                stroke="#2563EB"
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-6">
          <h3 className="text-base font-semibold text-text-primary mb-4">
            Daily Revenue (Last 30 Days)
          </h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.dailyRevenue}>
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11, fill: '#94A3B8' }}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fill: '#94A3B8' }}
                tickLine={false}
                axisLine={false}
                width={30}
              />
              <Tooltip
                contentStyle={{
                  background: '#fff',
                  border: '1px solid #E2E8F0',
                  borderRadius: 10,
                  fontSize: 13,
                }}
              />
              <Bar dataKey="amount" fill="#2563EB" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Recent Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-6">
          <h3 className="text-base font-semibold text-text-primary mb-4">Recent Signups</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left text-[11px] font-semibold text-text-hint uppercase tracking-wider pb-3">
                    Phone
                  </th>
                  <th className="text-left text-[11px] font-semibold text-text-hint uppercase tracking-wider pb-3">
                    Status
                  </th>
                  <th className="text-left text-[11px] font-semibold text-text-hint uppercase tracking-wider pb-3">
                    Joined
                  </th>
                </tr>
              </thead>
              <tbody>
                {(data.recentSignups || []).slice(0, 10).map((user) => (
                  <tr key={user.id} className="border-b border-border/50">
                    <td className="py-3 text-sm text-text-primary">{user.phone}</td>
                    <td className="py-3">
                      <Badge variant={statusVariant(user.subscriptionStatus)}>
                        {user.subscriptionStatus}
                      </Badge>
                    </td>
                    <td className="py-3 text-sm text-text-secondary">
                      {formatDate(user.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-base font-semibold text-text-primary mb-4">Recent Payments</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left text-[11px] font-semibold text-text-hint uppercase tracking-wider pb-3">
                    Phone
                  </th>
                  <th className="text-left text-[11px] font-semibold text-text-hint uppercase tracking-wider pb-3">
                    Amount
                  </th>
                  <th className="text-left text-[11px] font-semibold text-text-hint uppercase tracking-wider pb-3">
                    Date
                  </th>
                  <th className="text-left text-[11px] font-semibold text-text-hint uppercase tracking-wider pb-3">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {(data.recentPayments || []).slice(0, 10).map((sub) => (
                  <tr key={sub.id} className="border-b border-border/50">
                    <td className="py-3 text-sm text-text-primary">{sub.phone || '—'}</td>
                    <td className="py-3 text-sm text-text-primary font-medium">₹10</td>
                    <td className="py-3 text-sm text-text-secondary">
                      {formatDate(sub.createdAt)}
                    </td>
                    <td className="py-3">
                      <Badge variant={statusVariant(sub.status)}>{sub.status}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}
